import React, { useCallback, useEffect, useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import moment from "moment";
import { useSelector } from "react-redux";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";

function AddCompany() {
  const navigate = useNavigate();
  const isDarkMode = useSelector((state) => state.appSettings?.appDarkMode);
 
  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);
  
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndustryType, setSelectedIndustryType] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [companyData, setCompanyData] = useState({
    // From Company.cs
    compName: "",
    ownerName: "",
    legalCompanyName: "",
    compDetail: "",
    compEmail: "",
    compPhone: "",
    compFax: "",
    compIndustryType: "Hospitality",
    compCurrency: "",
    compCountry: "",
    compCity: "",
    compAddress: "",
    compSecCode: "",
    compType: "",
    compRemarks: "",
    compRegDate: moment().format("YYYY-MM-DD"), // Set to current date by default
    compExpireDate: null,
    compIcon: "",
    compHeaderLogo: "",
    compPNotificationLimit_PerDay: 10,
    compTimeZone: "GMT Standard Time",
    compUTCTimeZone: "(UTC+00:00) Dublin, Edinburgh, Lisbon, London",
    compLatitude: "0",
    compLongitude: "0",
    isNeedtoSyncDB: false,
    androidAppver: "",
    iosAppver: "",
    dailyForecastBracket: false,
    isArchieve: false,
    compNumberOfDaysToShowExpiryWarning: 0,
    compRecoveryEmail: "",
    compRecoveryPhoneNumber: "",
    compLanguage: "en",
    compWebsite: "",
    compFacebooklink: "",
    compInstagramLink: "",
    compYoutubeLink: "",
    compTikTokLink: "",
    compGoogleReview: "",
    compPublish: false,

    // From Company2.cs
    parentComID: 0,
    locationLicences: 0,
    isCallerID: false,
    isMobileApp: false,
    isKitchenApp: false,
    isWebApp: false,
    isTVApp: false,
    isWaiterApp: false,
    isOnlineOrderingAllowed: true,
    isReservationAllowed: true,
    compMaxIPadLicense: 1,
    waiterAppMaxDevices: 0,
    compMaxUserLicense: 1,
    compMonthlySmsLimit: 0,
    compMonthlyWhatsAppLimit: 0,
    // Set current date for hidden date fields to avoid DateTime.MinValue (1/1/0001) SQL errors
    mobileAppExpireDate: moment().format("YYYY-MM-DD"),
    webAppExpireDate: moment().format("YYYY-MM-DD"),
    waiterAppExpireDate: moment().format("YYYY-MM-DD"),
  });

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);

      if (response.data && response.data.data) {
        const currencyOptions = response.data.data.map((currency) => ({
          value: currency.value,
          label: currency.text,
        }));
        setCurrencies(currencyOptions);
      } else {
        // Fallback currencies if API fails
        const fallbackCurrencies = [
          { value: "USD", label: "US Dollar" },
          { value: "EUR", label: "Euro" },
          { value: "GBP", label: "British Pound" },
          { value: "AED", label: "UAE Dirham" },
        ];
        setCurrencies(fallbackCurrencies);
      }
    } catch (err) {
      showErrorToast("Failed to load currencies");
      // Fallback currencies if API fails
      const fallbackCurrencies = [
        { value: "USD", label: "US Dollar" },
        { value: "EUR", label: "Euro" },
        { value: "GBP", label: "British Pound" },
        { value: "AED", label: "UAE Dirham" },
      ];
      setCurrencies(fallbackCurrencies);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCompanyData({ ...companyData, [field]: value });
  };

  const handleDateChange = (newValue, field) => {
    setCompanyData({
      ...companyData,
      [field]: newValue ? moment(newValue).format("YYYY-MM-DD") : null,
    });
  };

  const handleCountryCityChange = (data) => {
    setCompanyData({
      ...companyData,
      ...data,
    });
    if (data.CompCountry !== undefined) {
      setSelectedCountry(data.CompCountry);
    }
    if (data.CompCity !== undefined) {
      setSelectedCity(data.CompCity);
    }
  };

  const handleSubmit = async () => {
    const {
      compName,
      ownerName,
      legalCompanyName,
      compEmail,
      compIndustryType,
      compCurrency,
    } = companyData;

    if (
      !compName ||
      !ownerName?.trim() ||
      !legalCompanyName?.trim() ||
      !compEmail ||
      !compIndustryType ||
      !compCurrency
    ) {
      showErrorToast("Please fill in all required fields (including Owner Name and Legal Company Name).");
      return;
    }

    try {
      setLoading(true);
      
      // Build payload - only include date fields if they have values
      const payload = {
        // Auto-set fields
        LocalizeID: "en",

        // From Company.cs
        CompName: companyData.compName,
        OwnerName: (companyData.ownerName || "").trim(),
        LegalCompanyName: (companyData.legalCompanyName || "").trim(),
        CompDetail: companyData.compDetail,
        CompEmail: companyData.compEmail,
        CompPhone: companyData.compPhone,
        CompFax: companyData.compFax,
        CompIndustryType: companyData.compIndustryType,
        CompCurrency: companyData.compCurrency,
        CompCountry: companyData.CompCountry || "",
        CompCity: companyData.CompCity || "",
        CompAddress: companyData.compAddress,
        CompSecCode: companyData.compSecCode,
        CompType: companyData.compType,
        CompRemarks: companyData.compRemarks,
        CompIcon: companyData.compIcon,
        CompHeaderLogo: companyData.compHeaderLogo,
        CompPNotificationLimit_PerDay: parseInt(companyData.compPNotificationLimit_PerDay) || 10,
        CompTimeZone: "GMT Standard Time",
        CompUTCTimeZone: "(UTC+00:00) Dublin, Edinburgh, Lisbon, London",
        CompLatitude: companyData.compLatitude,
        CompLongitude: companyData.compLongitude,
        IsNeedtoSyncDB: companyData.isNeedtoSyncDB || false,
        AndroidAppver: companyData.androidAppver,
        IOSAppver: companyData.iosAppver,
        DailyForecastBracket: companyData.dailyForecastBracket || false,
        IsArchieve: companyData.isArchieve,
        CompNumberOfDaysToShowExpiryWarning: parseInt(companyData.compNumberOfDaysToShowExpiryWarning) || 0,
        CompRecoveryEmail: companyData.compRecoveryEmail || "",
        CompRecoveryPhoneNumber: companyData.compRecoveryPhoneNumber || "",
        CompLanguage: companyData.compLanguage,
        CompWebsite: companyData.compWebsite || "",
        CompFacebooklink: companyData.compFacebooklink || "",
        CompInstagramLink: companyData.compInstagramLink || "",
        CompYoutubeLink: companyData.compYoutubeLink || "",
        CompTikTokLink: companyData.compTikTokLink || "",
        CompGoogleReview: companyData.compGoogleReview || "",
        CompPublish: companyData.compPublish,

        // From Company2.cs
        ParentComID: parseInt(companyData.parentComID) || 0,
        LocationLicences: parseInt(companyData.locationLicences) || 0,
        IsCallerID: companyData.isCallerID,
        ISMobileApp: companyData.isMobileApp,
        IsKitchenApp: companyData.isKitchenApp,
        ISWebApp: companyData.isWebApp,
        IsTVApp: companyData.isTVApp,
        IsWaiterApp: companyData.isWaiterApp,
        IsOnlineOrderingAllowed: companyData.isOnlineOrderingAllowed,
        IsReservationAllowed: companyData.isReservationAllowed,
        CompMaxIPadLicense: parseInt(companyData.compMaxIPadLicense) || 1,
        WaiterAppMaxDevices: parseInt(companyData.waiterAppMaxDevices) || 0,
        CompMaxUserLicense: parseInt(companyData.compMaxUserLicense) || 1,
        CompMonthlySmsLimit: parseInt(companyData.compMonthlySmsLimit) || 0,
        CompMonthlyWhatsAppLimit: parseInt(companyData.compMonthlyWhatsAppLimit) || 0,
      };

      // Helper function to validate date
      const isValidDate = (dateString) => {
        if (!dateString) return false;
        const date = moment(dateString, "YYYY-MM-DD", true);
        return date.isValid() && date.year() >= 1753 && date.year() <= 9999;
      };

      // CompRegDate: Always include with current date as default (with time component for SQL)
      const regDate = companyData.compRegDate || moment().format("YYYY-MM-DD");
      if (isValidDate(regDate)) {
        payload.CompRegDate = moment(regDate).format("YYYY-MM-DDTHH:mm:ss");
      } else {
        payload.CompRegDate = moment().format("YYYY-MM-DDTHH:mm:ss");
       }
      
      // CompExpireDate: Optional, only add if provided
      if (companyData.compExpireDate && isValidDate(companyData.compExpireDate)) {
        payload.CompExpireDate = moment(companyData.compExpireDate).format("YYYY-MM-DDTHH:mm:ss");
 
      }
      
      // Hidden date fields: Always include with current date to avoid DateTime.MinValue (1/1/0001) SQL errors
      payload.MobileAppExpireDate = companyData.mobileAppExpireDate 
        ? moment(companyData.mobileAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
 
      
      payload.WebAppExpireDate = companyData.webAppExpireDate 
        ? moment(companyData.webAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
 
      
      payload.WaiterAppExpireDate = companyData.waiterAppExpireDate 
        ? moment(companyData.waiterAppExpireDate).format("YYYY-MM-DDTHH:mm:ss")
        : moment().format("YYYY-MM-DDTHH:mm:ss");
 

      // Remove any null, undefined, or empty string values that might cause issues
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined || payload[key] === "") {
          console.log(`Removing empty field: ${key} = ${payload[key]}`);
          delete payload[key];
        }
      });
  
      const response = await axiosInstance.post(APIs.POST_ADD_COMPANY, payload);

      if (response.data.status) {
        showSuccessToast(response.data.message || "Company added successfully!");
        navigate("/company/manageCompany");
      } else {
        showErrorToast(response.data.message || "Failed to add company. Please check all fields.");
      }
    } catch (error) {
      console.error("Error adding company:", error);
      
      // Handle different error types
      if (error.response?.status === 404) {
        showErrorToast("Add company endpoint not found. Please check server configuration.");
      } else if (error.response?.status === 400) {
        // Validation error
        const errorData = error.response.data;
        if (errorData?.errors) {
          // Extract first error message
          const firstError = Object.values(errorData.errors)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          showErrorToast(errorMessage || "Validation error. Please check all fields.");
        } else if (errorData?.title) {
          showErrorToast(errorData.title);
        } else if (errorData?.message) {
          showErrorToast(errorData.message);
        } else {
          showErrorToast("Invalid data. Please check all required fields.");
        }
      } else if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else if (error.response?.data?.title) {
        showErrorToast(error.response.data.title);
      } else if (error.message) {
        showErrorToast(`Error: ${error.message}`);
      } else {
        showErrorToast("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company/manageCompany">Manage Company</Link>
            </li>
            <li className="breadcrumb-item active">Add Company</li>
          </ol>
          <h1 className="page-header mb-0">Add New Company</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn btn-success btn-rounded px-4 rounded-pill"
            disabled={loading}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
            Submit
          </button>
        </div>
      </div>

      <Panel>
        <PanelHeader>General</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Company Name *"
                placeHolder="Enter Company Name"
                type="text"
                value={companyData.compName}
                onchange={(e) => handleInputChange(e, "compName")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Owner Name *"
                placeHolder="Enter Owner Full Name"
                type="text"
                value={companyData.ownerName}
                onchange={(e) => handleInputChange(e, "ownerName")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Legal Company Name *"
                placeHolder="e.g. Indian Kitchen LTD"
                type="text"
                value={companyData.legalCompanyName}
                onchange={(e) => handleInputChange(e, "legalCompanyName")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Email *"
                placeHolder="Enter Email Address"
                type="email"
                value={companyData.compEmail}
                onchange={(e) => handleInputChange(e, "compEmail")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Phone Number"
                placeHolder="Enter Phone Number"
                type="tel"
                value={companyData.compPhone}
                onchange={(e) => handleInputChange(e, "compPhone")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Fax Number"
                placeHolder="Enter Fax Number"
                type="tel"
                value={companyData.compFax}
                onchange={(e) => handleInputChange(e, "compFax")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <label>Industry Type</label>
              <Select
                value={selectedIndustryType}
                onChange={(option) => {
                  setSelectedIndustryType(option);
                  setCompanyData({
                    ...companyData,
                    compIndustryType: option ? option.value : "",
                  });
                }}
                options={industryTypes}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Industry Type"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
            <div className="col-6">
              <label>Currency</label>
              <Select
                value={selectedCurrency}
                onChange={(option) => {
                  setSelectedCurrency(option);
                  setCompanyData({
                    ...companyData,
                    compCurrency: option ? option.value : "",
                  });
                }}
                options={currencies}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Currency"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
          </div>

          <div className="row mb-3">
            <CountryCitySelector
              selectedCountry={selectedCountry}
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              onChange={handleCountryCityChange}
              setLoading={setLoading}
              selectedValueIsLabel={true}
              isDarkMode={isDarkMode}
            />
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Address"
                placeHolder="Enter Company Address"
                type="text"
                value={companyData.compAddress}
                onchange={(e) => handleInputChange(e, "compAddress")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Code"
                placeHolder="Enter Company Code"
                type="text"
                value={companyData.compSecCode}
                onchange={(e) => handleInputChange(e, "compSecCode")}
                icon="fa-random"
                iconTitle="Generate random 6-digit code"
                iconOnClick={() => {
                  const code = String(Math.floor(100000 + Math.random() * 900000));
                  setCompanyData((prev) => ({ ...prev, compSecCode: code }));
                }}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Push Notification Limit Per Day"
                placeHolder="Enter Push Notification Limit"
                type="number"
                value={companyData.compPNotificationLimit_PerDay}
                onchange={(e) =>
                  handleInputChange(e, "compPNotificationLimit_PerDay")
                }
                step="10"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <h6>Remarks</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Remarks"
                value={companyData.compRemarks}
                onChange={(e) => handleInputChange(e, "compRemarks")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
            <div className="col-6">
              <h6>Company Detail</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Detail"
                value={companyData.compDetail}
                onChange={(e) => handleInputChange(e, "compDetail")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
          </div>
        </PanelBody>
      </Panel>


      <Panel>
        <PanelHeader>Licenses & Limits</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-4">
              <FormInput
                label="Device License"
                placeHolder="Enter Device License"
                type="number"
                value={companyData.compMaxIPadLicense}
                onchange={(e) => handleInputChange(e, "compMaxIPadLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Max User License"
                placeHolder="Enter Max User License"
                type="number"
                value={companyData.compMaxUserLicense}
                onchange={(e) => handleInputChange(e, "compMaxUserLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Location Licenses"
                placeHolder="Enter Location Licenses"
                type="number"
                value={companyData.locationLicences}
                onchange={(e) => handleInputChange(e, "locationLicences")}
                step="1"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-4">
              <FormInput
                label="Admin SMS credits (wallet grant)"
                placeHolder="One-time admin SMS credits (does not reset monthly)"
                type="number"
                value={companyData.compMonthlySmsLimit}
                onchange={(e) => handleInputChange(e, "compMonthlySmsLimit")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Monthly WhatsApp Segments"
                placeHolder="Enter monthly WhatsApp segment limit"
                type="number"
                value={companyData.compMonthlyWhatsAppLimit}
                onchange={(e) => handleInputChange(e, "compMonthlyWhatsAppLimit")}
                step="1"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-4">
              <FormInput
                label="Android App Version"
                placeHolder="Enter Android App Version"
                type="text"
                value={companyData.androidAppver}
                onchange={(e) => handleInputChange(e, "androidAppver")}
              />
            </div>
            <div className="col-4">
              <FormInput
                label="iOS App Version"
                placeHolder="Enter iOS App Version"
                type="text"
                value={companyData.iosAppver}
                onchange={(e) => handleInputChange(e, "iosAppver")}
              />
            </div>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>Dates & Settings</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration Start Date"
                value={companyData.compRegDate}
                onChange={(newValue) =>
                  handleDateChange(newValue, "compRegDate")
                }
                mode="date"
                placeholder="Select Date"
              />
            </div>
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration End Date"
                value={companyData.compExpireDate}
                onChange={(newValue) =>
                  handleDateChange(newValue, "compExpireDate")
                }
                mode="date"
                placeholder="Select Date"
              />
            </div>
          </div>

          <div className="row mb-3 mt-3">
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.compPublish}
                onToggle={(e) => handleInputChange(e, "compPublish")}
                label="Publish Company"
                name="compPublish"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.isOnlineOrderingAllowed}
                onToggle={(e) => handleInputChange(e, "isOnlineOrderingAllowed")}
                label="Online Ordering Allowed"
                name="isOnlineOrderingAllowed"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.isReservationAllowed}
                onToggle={(e) => handleInputChange(e, "isReservationAllowed")}
                label="Reservation Allowed"
                name="isReservationAllowed"
              />
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

export default AddCompany;